import { formatLocalDate } from '../../utils/localDateTime';
import React, { useState } from 'react';
import { IntegratedAnalysisResult, Page, NavParams } from '@types';
import { 
    md, 
    formatTimestamp, 
    Section, 
    Disclaimer, 
    sanitizeFirestoreData 
} from './ReportShared';
import RecommendedSuitesDisplay from './RecommendedSuitesDisplay';
import UnifiedMap from '../UnifiedMap';
import { getMapCoordinates } from '../../utils/mapCoordinates';

interface IntegratedAnalysisDisplayProps {
    result: IntegratedAnalysisResult;
    onNavigate?: (nav: { page: Page; params?: NavParams }) => void;
    isLoaded: boolean;
}

export const IntegratedAnalysisDisplay: React.FC<IntegratedAnalysisDisplayProps> = ({ result, onNavigate, isLoaded }) => {
    const [activePmesiiTab, setActivePmesiiTab] = useState('political');
    
    // FIX: Filter all map data sources for valid lat/lng
    const mapMarkers = [
        ...(result.events || []).flatMap(e => {
            const coordinates = getMapCoordinates(e);
            return coordinates ? [{
                ...e,
                id: e.id, ...coordinates, name: e.type, description: e.description, color: '#10b981', type: e.type
            }] : [];
        }),
        ...(result.routeData?.risks || []).flatMap((risk, index) => {
            const coordinates = getMapCoordinates(risk);
            return coordinates ? [{
                ...risk,
                id: `route-risk-${index}`, ...coordinates, name: 'Route Risk', description: risk.description, color: '#ef4444', type: 'Risk'
            }] : [];
        })
    ];

    const mapCircles = (result.conflictZones || []).flatMap(zone => {
        const coordinates = getMapCoordinates(zone);
        return coordinates ? [{
            ...zone, riskLevel: (zone as any).riskLevel || zone.severity,
            ...coordinates, radius: zone.radius || 5000, name: zone.name, color: zone.severity === 'Critical' ? '#ef4444' : '#f59e0b'
        }] : [];
    });
    
    const initialMapCenter = mapMarkers.length > 0 ? { lat: mapMarkers[0].lat, lng: mapMarkers[0].lng } : 
                              mapCircles.length > 0 ? { lat: mapCircles[0].lat, lng: mapCircles[0].lng } : 
                              { lat: 20, lng: 0 };

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* IntelOptics Branding & Identification Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b-2 border-slate-900 pb-4 mb-6">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <span className="bg-slate-900 text-white text-[10px] font-black px-2 py-0.5 rounded tracking-tighter uppercase">IntellOptics ECC</span>
                        <span className="text-slate-500 font-bold text-[10px] tracking-widest uppercase">System: 0x77-ALPHA</span>
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight leading-none uppercase">
                        {result.title || (result as any).scenario || 'Integrated Threat Analysis'}
                    </h2>
                    <div className="text-sm text-slate-500 font-medium mt-1 uppercase tracking-wider">
                        Integrated Strategic Analysis & Asset Monitoring
                    </div>
                </div>
                <div className="flex flex-col md:items-end">
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Document Registry</div>
                    <div className="text-sm font-bold text-slate-700">{formatTimestamp((result as any).date) || formatLocalDate(new Date())}</div>
                    <div className="text-[10px] text-slate-400 mt-1">
                        Last Updated: <span className="text-slate-600 font-bold tracking-tighter">{formatTimestamp((result as any).lastUpdated || (result as any).timestamp || (result as any).generatedAt) || 'LIVE'}</span>
                    </div>
                </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                <h3 className="font-bold text-slate-800 text-sm uppercase mb-2">Executive Summary</h3>
                <div className="prose prose-sm text-slate-700 max-w-none" dangerouslySetInnerHTML={{ __html: md.render(result.executiveSummary || result.summary || 'No summary provided.') }} />
            </div>
            
            <Section title="Geospatial Intelligence Overview">
                <div className="w-full h-96 rounded-md overflow-hidden border border-slate-300 relative">
                    <UnifiedMap
                        isLoaded={isLoaded}
                        markers={mapMarkers} 
                        circles={mapCircles} 
                        choroplethEnabled={!!(result as any).globalRiskData}
                        riskData={(result as any).globalRiskData || []} 
                        initialViewState={{ 
                            longitude: initialMapCenter.lng, 
                            latitude: initialMapCenter.lat, 
                            zoom: 4 // Reduced zoom for wider country view
                        }}
                    />
                </div>
            </Section>
            
            {result.pmesii && (
                 <Section title="PMESII-PT Analysis">
                    <div className="analysis-screen-only border-b border-slate-200 mb-4 overflow-x-auto">
                        <nav className="-mb-px flex space-x-4">
                            {Object.keys(result.pmesii).filter(key => {
                                const content = (result.pmesii as any)[key];
                                if (!content) return false;
                                const desc = typeof content === 'string' ? content : content.description;
                                return desc && desc.length > 10 && !desc.includes("No specific") && !desc.includes("N/A");
                            }).map((key) => (
                                <button
                                    key={key}
                                    onClick={() => setActivePmesiiTab(key)}
                                    className={`whitespace-nowrap pb-2 px-1 border-b-2 font-medium text-sm capitalize transition-colors ${
                                        activePmesiiTab === key
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                    }`}
                                >
                                    {key.replace(/([A-Z])/g, ' $1').trim()}
                                </button>
                            ))}
                        </nav>
                    </div>
                    <div className="analysis-screen-only bg-white p-5 border rounded-lg shadow-inner min-h-[200px]">
                        {typeof (result.pmesii as any)[activePmesiiTab] === 'object' ? (
                            <div className="space-y-4">
                                <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed">
                                    <div dangerouslySetInnerHTML={{ __html: md.render((result.pmesii as any)[activePmesiiTab].description || '') }} />
                                </div>
                                {((result.pmesii as any)[activePmesiiTab].sources || []).length > 0 && (
                                    <div className="mt-6 pt-6 border-t border-slate-100">
                                        <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 12l2 2 4-4" /></svg>
                                            Verified Intelligence Sources
                                        </h5>
                                        <div className="flex flex-wrap gap-2">
                                            {(result.pmesii as any)[activePmesiiTab].sources.map((src: any, i: number) => {
                                                const sName = (typeof src === 'object' && src !== null) ? (src.stringValue || JSON.stringify(src)) : src;
                                                return (
                                                    <div key={i} className="flex flex-col gap-1 bg-slate-50 px-3 py-2 rounded border border-slate-200">
                                                        <span className="text-xs font-bold text-slate-800">{sName}</span>
                                                        {(result.pmesii as any)[activePmesiiTab].accessEndpoints?.[i] && (
                                                            <a 
                                                                href={(result.pmesii as any)[activePmesiiTab].accessEndpoints[i]} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                className="text-[10px] text-blue-600 hover:text-blue-800 underline truncate max-w-[200px]"
                                                            >
                                                                Access Point
                                                            </a>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed">
                                <div dangerouslySetInnerHTML={{ __html: md.render((result.pmesii as any)[activePmesiiTab] || '') }} />
                            </div>
                        )}
                    </div>
                    <div className="analysis-print-only space-y-4">
                        {Object.entries(result.pmesii).filter(([, content]: [string, any]) => {
                            const description = typeof content === 'string' ? content : content?.description;
                            return description && description.length > 10 && !description.includes('No specific') && !description.includes('N/A');
                        }).map(([key, content]: [string, any]) => (
                            <div key={key} className="bg-white border rounded-lg p-5 shadow-inner">
                                <h4 className="mb-3 text-sm font-black uppercase tracking-wider text-blue-700">{key.replace(/([A-Z])/g, ' $1').trim()}</h4>
                                <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: md.render(typeof content === 'string' ? content : content.description || '') }} />
                                {typeof content === 'object' && content?.sources?.length > 0 && (
                                    <div className="mt-4 border-t border-slate-100 pt-4">
                                        <h5 className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-400">Verified Intelligence Sources</h5>
                                        <ul className="space-y-1 text-xs text-slate-700">
                                            {content.sources.map((source: any, index: number) => <li key={index}>{typeof source === 'object' ? source.stringValue || JSON.stringify(source) : source}</li>)}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </Section>
             )}

            {(result.usEmbassy || result.visaRequirements || result.whoMedicalSummary) && (
                <Section title="Diplomatic, Travel & Health Intelligence">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* US Embassy */}
                        {result.usEmbassy && (
                            <div className="bg-slate-900 rounded-xl p-5 border border-slate-700 shadow-xl flex flex-col">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-blue-600 rounded-lg text-white">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                    </div>
                                    <h4 className="text-sm font-black text-white uppercase tracking-wider">US Embassy Details</h4>
                                </div>
                                <div className="space-y-4 flex-grow">
                                    <div>
                                        <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Office Location</span>
                                        <p className="text-xs text-slate-300 font-medium leading-relaxed">{result.usEmbassy.address || 'N/A'}</p>
                                    </div>
                                    <div className="bg-red-500/10 p-3 rounded-lg border border-red-500/20">
                                        <span className="text-[9px] font-black text-red-400 uppercase tracking-widest block mb-1">Emergency Contact</span>
                                        <p className="text-sm font-black text-white">{result.usEmbassy.emergencyContact || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Visa Entry */}
                        {result.visaRequirements && (
                            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm flex flex-col">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                    </div>
                                    <h4 className="text-sm font-black text-slate-800 uppercase tracking-wider">Visa Requirements</h4>
                                </div>
                                <div className="space-y-3 flex-grow scrollbar-hide overflow-y-auto max-h-48 text-xs text-slate-600 leading-relaxed font-medium">
                                    {result.visaRequirements.summary}
                                </div>
                                <div className="mt-4 pt-4 border-t border-slate-100">
                                    <a 
                                        href={result.visaRequirements.stateDeptLink || "https://travel.state.gov/content/travel/en/international-travel/International-Travel-Country-Information-Pages.html"} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline flex items-center gap-2"
                                    >
                                        State Dept Info
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                    </a>
                                </div>
                            </div>
                        )}

                        {/* WHO Medical */}
                        {result.whoMedicalSummary && (
                            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100 shadow-sm flex flex-col">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-emerald-600 rounded-lg text-white">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                                    </div>
                                    <h4 className="text-sm font-black text-emerald-900 uppercase tracking-wider">Medical & WHO Advisory</h4>
                                </div>
                                <div className="space-y-3 flex-grow text-xs text-emerald-800 leading-relaxed font-medium">
                                    {result.whoMedicalSummary.summary}
                                </div>
                                <div className="mt-4 pt-4 border-t border-emerald-200">
                                    <a 
                                        href={result.whoMedicalSummary.whoLink || "https://www.who.int/countries"} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-[10px] font-black text-emerald-700 uppercase tracking-widest hover:underline flex items-center gap-2"
                                    >
                                        WHO Country Page
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </Section>
            )}

            {/* Sections Filtering */}
            {(result.sections || [])
                .filter((section: any) => {
                    const sTitle = (section.title || '').toLowerCase();
                    const isPmesii = sTitle.includes('pmesii');
                    const isScenario = sTitle.includes('scenario');
                    const isConfidence = sTitle.includes('confidence');
                    const isStability = sTitle.includes('government stability');
                    return !(isPmesii || isScenario || isConfidence || isStability);
                })
                .map((section: any, index: number) => (
                    <Section 
                        key={index} 
                        title={section.title || `Intelligence Detail ${index + 1}`}
                    >
                        <div 
                            className="prose prose-slate max-w-none text-slate-700" 
                            dangerouslySetInnerHTML={{ __html: md.render(section.content || '') }} 
                        />
                    </Section>
                ))
            }

            {/* Threat Actors */}
            <Section title="Identified Threat Actors">
                <div className="space-y-4">
                    {((result as any).actors || (result as any).threatGroups || []).map((actor: any, index: number) => {
                        const sanitizedActor = sanitizeFirestoreData(actor);
                        const aName = sanitizedActor.name || 'Unknown Actor';
                        const aType = sanitizedActor.type || 'General';
                        const aDesc = sanitizedActor.description || '';

                        return (
                            <div key={index} className="p-4 bg-slate-50 rounded-lg border">
                                <h5 className="font-bold text-slate-800">{aName} <span className="text-xs font-medium bg-slate-200 px-2 py-0.5 rounded-full">{aType}</span></h5>
                                <p className="text-sm text-slate-600 mt-1">{aDesc}</p>
                            </div>
                        );
                    })}
                </div>
            </Section>

            {/* Intelligence Sources */}
            {result.sources && result.sources.length > 0 && (
                <Section title="Intelligence Sources & References">
                    <div className="bg-slate-900 rounded-xl overflow-hidden border border-slate-800 shadow-2xl">
                        <div className="bg-slate-800/50 px-4 py-2 border-b border-slate-700 flex justify-between items-center">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Source Registry</span>
                            <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-500 text-[9px] font-bold rounded border border-emerald-500/20 uppercase tracking-tighter animate-pulse">
                                Integrity Verified
                            </span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-xs text-left">
                                <thead className="text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-slate-800">
                                    <tr>
                                        <th className="px-4 py-3">Analytical Area</th>
                                        <th className="px-4 py-3">Verified Source</th>
                                        <th className="px-4 py-3 text-right">Validated Access Point</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800/50">
                                    {result.sources.map((src: any, i: number) => {
                                        const sanitizedSrc = sanitizeFirestoreData(src);
                                        const area = sanitizedSrc.assessmentArea || sanitizedSrc.title || 'General Intelligence';
                                        const name = sanitizedSrc.sourceName || sanitizedSrc.title || 'Verified Partner';
                                        const url = sanitizedSrc.url || sanitizedSrc.uri;
                                        
                                        return (
                                            <tr key={i} className="hover:bg-slate-800/30 transition-colors group">
                                                <td className="px-4 py-3 text-slate-400 font-bold uppercase tracking-tighter">
                                                    {area}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex flex-col">
                                                        <span className="text-slate-200 font-black">{name}</span>
                                                        <span className="text-[9px] text-slate-500 uppercase">Registry Code: {Math.random().toString(36).substring(7).toUpperCase()}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <a 
                                                        href={url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1 text-indigo-400 hover:text-indigo-300 font-bold transition-all group-hover:translate-x-1"
                                                    >
                                                        Secure URL 
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                                    </a>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Section>
            )}

            {/* Strategic Next Steps */}
            {onNavigate && result.recommendedSuites && (
                <RecommendedSuitesDisplay 
                    suites={result.recommendedSuites} 
                    onNavigate={onNavigate} 
                    contextCountry={result.country}
                />
            )}
            
            {result.additionalSections && result.additionalSections.length > 0 && (
                <div className="space-y-6">
                    {result.additionalSections.map((section: any, index: number) => (
                        <Section key={index} title={section.title || section.heading || 'Intelligence Directive'}>
                            <div className="prose prose-slate max-w-none text-slate-700 font-medium" dangerouslySetInnerHTML={{ __html: md.render(section.content || '') }} />
                        </Section>
                    ))}
                </div>
            )}
            
            <Disclaimer />
        </div>
    );
};

export default IntegratedAnalysisDisplay;
